import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAxios, postAxios } from '../../axios/Axios';
import request from '../../request/request';
import Loader from '../loader/Loader';
import { ToastTypes } from '../constants/Constants';
import {Divider} from "primereact/divider" ;
import { useGlobal } from '../../context/GlobalContext';

const ManageSEO = ({ changeToast }) => {
	const location = useLocation();
	const data = location.state;
	const { setDashboardTitle } = useGlobal();
	const [loading, setLoading] = useState(false);
	const [webAnalyticsFields, setWebAnalyticsFields] = useState([]);
	const [formValues, setFormValues] = useState({});
	const [isEdit, setIsEdit] = useState(false);

	const fetchWebAnalytics = useCallback(async () => {
		try {
			const res = await getAxios(
				`${request.webAnalyticsType}?parent_type_id=WEB_ANALYTICS`
			);

			if (res?.data?.apiresponse?.type === 'OK') {
				setWebAnalyticsFields(res?.data?.items);
			}
		} catch (error) {
			console.log('Error fetching web analytics: ', error);
		}
	}, []);

	const fetchExistingSEO = useCallback(async () => {
		try {
			setLoading(true);
			const res = await getAxios(
				`${request.getWebsiteMetaTags}?website_id=${data?.website_id}`
			);
			if (res?.data?.apiresponse?.type === 'OK') {
				const existingData = res?.data?.records || [];
				if (existingData.length > 0) {
					setIsEdit(true);
					const formatted = {};
					existingData.forEach((item) => {
						formatted[item.web_analytics_type_id] = item.web_analytics_code;
					});
					setFormValues(formatted);
				}
			}
		} catch (error) {
			console.log('Error fetching existing SEO data: ', error);
		} finally {
			setLoading(false);
		}
	}, [data?.website_id]);

	useEffect(() => {
		fetchWebAnalytics();
	}, [fetchWebAnalytics]);

	useEffect(() => {
		if (data?.website_id) {
			fetchExistingSEO();
		}
	}, [fetchExistingSEO, data?.website_id]);

	const handleChange = (e) => {
		const { name, value } = e.target;

		setFormValues((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		try {
			setLoading(true);

			const metatags = Object.keys(formValues).map((key) => ({
				meta_type: key,
				content: formValues[key],
			}));

			const payload = {
				website_id: data?.website_id,
				metatags,
			};

			const res = await postAxios(request.addUpdateWebsiteMetaTags, payload);

			if (res?.data?.apiresponse?.type === 'OK') {
				changeToast(ToastTypes.TOAST_SUCCESS, ToastTypes.TOAST_SUCCESS_TITLE, res?.data?.apiresponse?.message);
			} else {
			changeToast( ToastTypes.TOAST_ERROR, ToastTypes.TOAST_ERROR_TITLE, res?.data?.apiresponse?.message);
			}
		} catch (error) {
			console.log('Error while submitting manage seo form: ', error);
			changeToast( ToastTypes.TOAST_ERROR, ToastTypes.TOAST_ERROR_TITLE, 'Error submitting SEO form');
			
		} finally {
			setLoading(false);
		}
	};

	const url =
		data?.website_host.startsWith('http://') ||
		data?.website_host.startsWith('https://')
			? data?.website_host
			: `http://${data?.website_host}`;

			const handleCopy = async (value) => {
				try {
					if (!value) {
						changeToast(
							ToastTypes.TOAST_ERROR,
							ToastTypes.TOAST_ERROR_TITLE,
							'Nothing to copy'
						);
						return;
					}
			
					// Modern secure method
					if (navigator.clipboard && window.isSecureContext) {
						await navigator.clipboard.writeText(value);
					} else {
						// Fallback method
						const textArea = document.createElement('textarea');
						textArea.value = value;
						textArea.style.position = 'fixed';
						textArea.style.opacity = '0';
						document.body.appendChild(textArea);
						textArea.focus();
						textArea.select();
			
						const successful = document.execCommand('copy');
						document.body.removeChild(textArea);
			
						if (!successful) {
							throw new Error('Fallback copy failed');
						}
					}
			
					changeToast(
						ToastTypes.TOAST_SUCCESS,
						ToastTypes.TOAST_SUCCESS_TITLE,
						'Copied to clipboard'
					);
				} catch (error) {
					console.error('Copy failed:', error);
			
					changeToast(
						ToastTypes.TOAST_ERROR,
						ToastTypes.TOAST_ERROR_TITLE,
						'Copy failed. Please copy manually.'
					);
				}
			};
			
	useEffect(()=> {
		setDashboardTitle('Manage SEO');
		}, [])
	return (
		<>
			<div className="mapped-screen-details">
				<div className="d-flex gap-5">
					<i className='pi pi-arrow-circle-left big-icon' onClick={()=>window.history.back()} />
					<div className="mapped-screen-data">
						<div className="mapped-screen-title">Website Name</div>
						<div className="d-flex align-items-center gap-1">
							<div className="mapped-screen-name">{data?.website_name}</div>
							<div className="mapped-screen-tipdata">({data?.section})</div>
						</div>
					</div>

					<div className="mapped-screen-data">
						<div className="mapped-screen-title">Website Path</div>
						<div className="mapped-screen-name">{data?.webapp_path}</div>
					</div>

					<div className="mapped-screen-data">
						<div className="mapped-screen-title">URL</div>
						<a href={url} target="_blank" rel="noopener noreferrer">
							{data?.website_host}
						</a>
					</div>
				</div>
			</div>

			<Divider />

			<div className="mapped-screen-container d-flex gap-2 flex-row">
				{loading && <Loader />}

				<form onSubmit={handleSubmit} className="w-100">
					{webAnalyticsFields.map((field) => (
						<div
							className="mb-3 position-relative"
							key={field.web_analytics_type_id}
						>
							<label className="form-label">{field?.description}</label>

							<div className="position-relative">
								<textarea
									className="form-control pe-5"
									placeholder={field?.description}
									name={field?.web_analytics_type_id}
									value={formValues[field.web_analytics_type_id] || ''}
									onChange={handleChange}
									rows={4}
								/>

								<button
									type="button"
									className="btn btn-sm btn-light position-absolute top-0 end-0 mt-2 me-2 border"
									onClick={() =>
										handleCopy(formValues[field.web_analytics_type_id])
									}
									title="Copy"
								>
									<i className="pi pi-copy" />
								</button>
							</div>
						</div>
					))}

					<button
						type="submit"
						className="btn btn-primary mt-3"
						disabled={loading}
					>
						{isEdit ? 'Update SEO' : 'Save SEO'}
					</button>
				</form>
			</div>
		</>
	);
};

export default ManageSEO;
